import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../config/env.js';

const baseURL = config.apiBaseUrl;

/** How long the same (endpoint, status) pair stays suppressed, in ms. */
const DEDUPE_WINDOW_MS = 60 * 1000;
/** Ceiling on `/log` posts within one dedupe window, across all endpoints. */
const MAX_LOGS_PER_WINDOW = 20;

/** @type {Map<string, number>} dedupe key -> timestamp of the last post */
const lastPostedAt = new Map();
let windowStartedAt = 0;
let postsInWindow = 0;

/**
 * Decides whether this failure should produce a `POST /log`.
 *
 * `/log` fires 1:1 with failures, so a failing poller turns a 5s interval into
 * a second stream of requests at the same rate. Collapsing by
 * (endpoint, status) keeps the first report of each distinct failure and drops
 * the repeats, and the window cap stops a burst of distinct failures - a
 * dropped connection mid-bootstrap - from amplifying on its own.
 *
 * @param {string} endpoint request url, cache-buster stripped
 * @param {number|string|undefined} statusCode http status, if the request got one
 * @returns {boolean} true when the caller should post
 */
const shouldLog = (endpoint, statusCode) => {
  const now = Date.now();

  if (now - windowStartedAt > DEDUPE_WINDOW_MS) {
    windowStartedAt = now;
    postsInWindow = 0;
  }
  if (postsInWindow >= MAX_LOGS_PER_WINDOW) return false;

  const key = `${statusCode ?? 'none'} ${String(endpoint || '').split('?')[0]}`;
  const seenAt = lastPostedAt.get(key);
  if (seenAt && now - seenAt < DEDUPE_WINDOW_MS) return false;

  lastPostedAt.set(key, now);
  postsInWindow += 1;
  return true;
};

/**
 * Logs an error to Slack via the backend API.
 *
 * Throttled and de-duplicated by (endpoint, status) - see `shouldLog`.
 *
 * @param {Object} err - The error object to log.
 * @param {string} [token] - The authorization token (optional).
 */
export const logErrorToSlack = (err, token) => {
  try {
    if (
      !shouldLog(
        err?.response?.data?.path || err?.config?.url,
        err?.response?.status,
      )
    ) {
      return;
    }
    const requestHeaders = err?.config?.headers;
    const objkeys = Object.keys(requestHeaders || {});
    const filteredKeys = objkeys.filter((key) => key !== 'Authorization');
    const filteredRequestHeaders = filteredKeys.reduce((curr, acc) => {
      return Object.assign(curr, { [acc]: requestHeaders[acc] });
    }, {});

    const parsedBody = JSON.parse(err?.config?.data || '{}');
    const msg = {
      errorText: err?.response?.data?.error,
      errorType: err?.response?.statusText,
      requestBody:
        typeof parsedBody === 'object' && !Array.isArray(parsedBody)
          ? Object.keys(parsedBody || {})
              ?.filter((key) => key !== 'password' && key !== 'gmailPass')
              ?.reduce((curr, acc) => {
                return Object.assign(curr, { [acc]: parsedBody[acc] });
              }, {})
          : err?.config?.data,
      requestMethod: err?.config?.method,
      requestParams: err?.config?.params,
      requestHeaders: filteredRequestHeaders,
      severity: 'error',
      statusCode: err?.response?.status,
      api: err?.response?.data?.path || err?.config?.url,
    };

    const reqId = uuidv4();
    axios
      .post(
        `${baseURL}log`,
        {
          message: '',
          data: {
            formatted: msg,
          },
        },
        {
          params: {
            'request-id': reqId,
          },
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        },
      )
      .catch((e) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to log to slack:', e);
        }
      });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Failed to prepare slack log:', e);
    }
  }
};
